import type { Routes } from '@angular/router';
import { type ActivatedRouteSnapshot } from '@angular/router';
import {
    createActionTriggerDealer,
    createMetaResolverWithDealer,
    DealerFeature,
    defaultTitleResolver,
    extraDealerTitleResolver,
    ModelsFeature,
    ngrxLoaderGuard,
    PageContentType,
} from '@hyundai/ng-common-lib';
import { provideEffects } from '@ngrx/effects';
import { provideState } from '@ngrx/store';
import { filter, firstValueFrom, map } from 'rxjs';

export default [
    {
        path: '',
        loadComponent: () => import('./model-list-page/model-list-page.component'),
        canActivate: [
            ngrxLoaderGuard({
                waitUntilLoadedSelector: ModelsFeature.state.selectIsLoadingModelList,
                dataToCheckSelector: ModelsFeature.state.selectModelList,
            }),
        ],
        providers: [
            provideState(ModelsFeature.state),
            provideEffects([ModelsFeature.effects]),
            createActionTriggerDealer(ModelsFeature.actions.loadModelList()),
        ],

        title: defaultTitleResolver('Alle Hyundai Modelle'),
        resolve: {
            meta: createMetaResolverWithDealer((dealer) => {
                return {
                    description: `Entdecken Sie unsere Hyundai Modelle bei ${dealer.name} ${dealer.city}. Besuchen Sie uns jetzt und vereinbaren Ihre Probefahrt!`,
                };
            }),
        },
        data: {
            contentType: PageContentType.MODELOVERVIEW,
        },
    },
    {
        path: ':modelSlug',
        loadComponent: () => import('./model-detail-page/model-detail-page.component'),
        canActivate: [
            ngrxLoaderGuard({
                actionToDispatch: (route: ActivatedRouteSnapshot) =>
                    ModelsFeature.actions.loadModelDetail({ slug: route.params['modelSlug'] }),
                waitUntilLoadedSelector: ModelsFeature.state.selectIsLoadingModelDetail,
                dataToCheckSelector: ModelsFeature.state.selectModelDetail,
                redirectOnFailUrl: (route, store) => {
                    return store.select(DealerFeature.state.selectDealerSlug).pipe(
                        map((slug) => {
                            return `${slug}/404`;
                        }),
                    );
                },
            }),
        ],
        providers: [provideState(ModelsFeature.state), provideEffects([ModelsFeature.effects])],
        title: extraDealerTitleResolver(async (store) => {
            const model = await firstValueFrom(
                store.select(ModelsFeature.state.selectModelDetail).pipe(filter((m) => !!m)),
            );
            return model.name;
        }),
        resolve: {
            meta: createMetaResolverWithDealer(async (dealer, store) => {
                const model = await firstValueFrom(
                    store.select(ModelsFeature.state.selectModelDetail).pipe(filter((m) => !!m)),
                );
                return {
                    description: `Entdecken Sie den ${model.name} bei ${dealer.name} ${dealer.city} und vereinbaren jetzt Ihre Probefahrt!`,
                };
            }),
        },
        data: {
            contentType: PageContentType.MODELDETAIL,
        },
    },
] satisfies Routes;
